import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { CihPullQuoteBand } from "~/components/cih/pull-quote-band";
import { CoachAboutSection } from "~/components/coach-profile/about-section";
import { CoachAlsoSection } from "~/components/coach-profile/also-section";
import { CoachApproachSection } from "~/components/coach-profile/approach-section";
import { CoachClosingCta } from "~/components/coach-profile/closing-cta";
import { CoachCredentialsStrip } from "~/components/coach-profile/credentials-strip";
import { getFeaturedTestimonial } from "~/components/coach-profile/featured-testimonial";
import { CoachHelpsWithSection } from "~/components/coach-profile/helps-with-section";
import { CoachOfferSection } from "~/components/coach-profile/offer-section";
import { CoachOutcomesSection } from "~/components/coach-profile/outcomes-section";
import {
  computeCoachSectionNumbers,
  type CoachSectionNumbers,
} from "~/components/coach-profile/section-numbering";
import { CoachSocialsRow } from "~/components/coach-profile/socials-row";
import { CoachTestimonialsSection } from "~/components/coach-profile/testimonials-section";
import { coachCihBackRefsQueryOptions } from "~/lib/functions/cih/coaches";
import { coachQueryOptions } from "~/lib/query-options";
import { coachWheelPath } from "~/lib/shared/coach-wheel";
import type { CoachPublicData } from "~/shared/loaders";

type ProfiledCoach = CoachPublicData & {
  profile: NonNullable<CoachPublicData["profile"]>;
};

type Props = {
  coach: ProfiledCoach;
  /** The coach's Wheel of Life page is live: "Also from me" links to it. */
  hasWheelPage?: boolean;
  /**
   * Renders the profile's tab navigation above the first section. The tabs
   * follow the section numbering, which this component computes.
   */
  tabNav?: (context: {
    sectionNumbers: CoachSectionNumbers;
    credentialCount: number;
  }) => ReactNode;
};

/**
 * Numbered section stack of a coach profile, then the socials row and the
 * closing call to action. Shared by the main profile (`/$username`) and the
 * discovery profile (`/discovery/$username`), so a new section is added once.
 * It loads the coach's Content Hub back-references and other offers itself;
 * the route loaders prefetch both so the server render already has them.
 *
 * Aliases: profile body, profile section stack
 */
export function CoachProfileSections({
  coach,
  hasWheelPage = false,
  tabNav,
}: Props) {
  const { data: cihBackRefs } = useQuery(
    coachCihBackRefsQueryOptions(coach.id),
  );

  const { data: otherOffers = [] } = useQuery(
    coachQueryOptions.otherOffers(coach.id, coach.membershipPlan ?? undefined),
  );

  const featured = getFeaturedTestimonial(coach.testimonials ?? []);

  const credentialCount =
    (coach.ccoMemberships?.length ?? 0) +
    (coach.ctpCertifications?.length ?? 0) +
    (coach.certifications?.length ?? 0);

  const sectionNumbers = computeCoachSectionNumbers({
    profile: coach.profile,
    mainOffer: coach.mainOffer,
    results: coach.results,
    testimonials: coach.testimonials,
    featuredTestimonial: featured,
    cihChallenges: cihBackRefs?.challenges ?? [],
    cihArticles: cihBackRefs?.articles ?? [],
    cihFrameworks: cihBackRefs?.frameworks ?? [],
    credentialCount,
    hasWheelPage,
  });

  return (
    <>
      {tabNav?.({ sectionNumbers, credentialCount })}

      {sectionNumbers.about !== undefined && (
        <CoachAboutSection
          firstName={coach.firstName}
          description={coach.profile.description}
          avatarUrl={coach.profilePictureUrl}
          sectionNumber={sectionNumbers.about}
        />
      )}

      {sectionNumbers.helpsWith !== undefined && (
        <CoachHelpsWithSection
          firstName={coach.firstName}
          challengesHeadline={coach.profile.challengesHeadline}
          challengesDescription={coach.profile.challengesDescription}
          cihChallenges={cihBackRefs?.challenges ?? []}
          sectionNumber={sectionNumbers.helpsWith}
        />
      )}

      {sectionNumbers.outcomes !== undefined && (
        <CoachOutcomesSection
          outcomesHeadline={coach.profile.outcomesHeadline}
          outcomesDescription={coach.profile.outcomesDescription}
          coachingResults={coach.results ?? []}
          sectionNumber={sectionNumbers.outcomes}
        />
      )}

      {featured && (
        <CihPullQuoteBand
          quote={featured.content}
          attribution={featured.author}
        />
      )}

      {sectionNumbers.approach !== undefined && (
        <CoachApproachSection
          whyTitle={coach.profile.whyTitle}
          whyContent={coach.profile.whyContent}
          sectionNumber={sectionNumbers.approach}
        />
      )}

      {sectionNumbers.coaching !== undefined && (
        <CoachOfferSection
          coach={coach}
          mainOffer={coach.mainOffer}
          otherOffers={otherOffers}
          sectionNumber={sectionNumbers.coaching}
        />
      )}

      {sectionNumbers.credentials !== undefined && (
        <CoachCredentialsStrip
          ccoMemberships={coach.ccoMemberships ?? []}
          ctpCertifications={coach.ctpCertifications ?? []}
          certifications={coach.certifications ?? []}
          sectionNumber={sectionNumbers.credentials}
        />
      )}

      {sectionNumbers.testimonials !== undefined && (
        <CoachTestimonialsSection
          testimonials={coach.testimonials ?? []}
          sectionNumber={sectionNumbers.testimonials}
        />
      )}

      {sectionNumbers.also !== undefined && (
        <CoachAlsoSection
          articles={cihBackRefs?.articles ?? []}
          frameworks={cihBackRefs?.frameworks ?? []}
          leadMagnet={{
            title: coach.profile.leadMagnetTitle,
            description: coach.profile.leadMagnetDescription,
            ctaText: coach.profile.leadMagnetCtaText,
            url: coach.profile.leadMagnetUrl,
          }}
          wheelPageHref={hasWheelPage ? coachWheelPath(coach.username) : null}
          sectionNumber={sectionNumbers.also}
        />
      )}

      <CoachSocialsRow firstName={coach.firstName} profile={coach.profile} />

      <CoachClosingCta coach={coach} />
    </>
  );
}
